import { useTranslation } from '@web_opxp/features';
import React from 'react';
import { connect } from 'react-redux';
import { formatDate } from '../../base/dateUtils';
import { CyclesActions, CyclesSelectors } from '../../cycles';
import { GroupSelectors } from '../../groupAdmin';
import { IState } from '../../store/state';
import { ITrainingStatistics } from '../types';
import { getTimeFromSeconds } from '../utils';
import StatisticsIcon from './StatisticsIcon';
import './StatisticsSection.scss';

interface IStatisticsSectionOwnProps {
  userId?: number;
}

interface IStatisticsSectionProps extends IStatisticsSectionOwnProps {
  stats: ITrainingStatistics;
  loadStats: () => void;
}

export const StatisticsSectionFC: React.FC<IStatisticsSectionProps> = ({
  loadStats,
  stats,
  userId,
}: IStatisticsSectionProps) => {
  const { language, t } = useTranslation();

  React.useEffect(() => {
    if (!userId && typeof stats.numberWorkouts === 'undefined') {
      loadStats();
    }
  }, [loadStats, stats, userId]);

  const accountCreationDate =
    stats.firstTimestamp && stats.firstTimestamp > 0 ? formatDate(stats.firstTimestamp, language) : '';
  const lastTraining = stats.lastTimestamp && stats.lastTimestamp > 0 ? formatDate(stats.lastTimestamp, language) : '';
  const { hours, minutes } = getTimeFromSeconds((stats.numberWorkouts ?? 0) * 90);

  return (
    <div className="statistics-section">
      <h3 className="statistics-section-title">{t('Evaluation.Overview.StatisticsSection.Title')}</h3>
      <div className="statistics-section-content-wrapper">
        {accountCreationDate && (
          <div className="row statistics-row">
            <div className="col-1">
              <StatisticsIcon icon="calendar" />
            </div>
            <div className="col-7">
              <p>{t('Evaluation.Overview.StatisticsSection.AccountCreationDate')}</p>
            </div>
            <div className="col-4">
              <p>{accountCreationDate}</p>
            </div>
          </div>
        )}
        <div className="row statistics-row">
          <div className="col-1">
            <StatisticsIcon icon="checkmark" />
          </div>
          <div className="col-7">
            <p>{t('Evaluation.Overview.StatisticsSection.Sessions')}</p>
          </div>
          <div className="col-4">
            <p>{stats.finishedSessions}</p>
          </div>
        </div>
        <div className="row statistics-row">
          <div className="col-1">
            <StatisticsIcon icon="clock" />
          </div>
          <div className="col-7">
            <p>{t('Evaluation.Overview.StatisticsSection.Time')}</p>
          </div>
          <div className="col-4">
            <p>{t('Evaluation.Overview.StatisticsSection.TimeValue', { hours, minutes })}</p>
          </div>
        </div>
        {lastTraining && (
          <div className="row statistics-row">
            <div className="col-1">
              <StatisticsIcon icon="dumbell" />
            </div>
            <div className="col-7">
              <p>{t('Evaluation.Overview.StatisticsSection.LastTraining')}</p>
            </div>
            <div className="col-4">
              <p>{lastTraining}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IState, ownProps: IStatisticsSectionOwnProps) => {
  const userId = ownProps.userId;
  let stats: ITrainingStatistics;
  if (!userId) {
    const { finishedSessions, firstTimestamp, lastTimestamp, numberWorkouts, currentCycleId } =
      CyclesSelectors.getCurrentCycleStats(state);
    if (!currentCycleId) {
      stats = {};
    } else {
      stats = {
        finishedSessions,
        firstTimestamp,
        lastTimestamp,
        numberWorkouts,
      };
    }
  } else {
    const groupMemberStats = GroupSelectors.getGroupMemberTrainingStats(state, userId);
    if (!groupMemberStats) {
      stats = {};
    } else {
      const { finishedSessions, firstTimestamp, lastTimestamp, numberWorkouts } = groupMemberStats;
      stats = {
        finishedSessions,
        firstTimestamp,
        lastTimestamp,
        numberWorkouts,
      };
    }
  }
  return {
    stats,
  };
};

export const mapDispatchToProps = {
  loadStats: CyclesActions.loadCycles,
};

const StatisticsSection = connect(mapStateToProps, mapDispatchToProps)(StatisticsSectionFC);
export default StatisticsSection;
